import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory } from 'react-router-dom';
import { List, Map } from 'immutable';
import { Popover, PopoverPanel } from '@headlessui/react';

import { createAndShowChatSessionWithMessage } from 'flow/actions/chat-actions';
import BackButton from 'flow/components/ui-components/BackButton';
import Button from 'flow/components/ui-components/Button';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Icon from 'flow/components/ui-components/Icon';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useAutoFocus from 'flow/hooks/useAutoFocus';
import useExtension from 'flow/hooks/useExtension';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { Capabilities } from 'flow/utils/capability-utils';
import { ChatMember, ChatSession } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { extractAbbreviatedName, extractName } from 'flow/utils';
import {
    filterByAllfileds,
    getExtensionHasCapability,
} from 'flow/utils/extension-utils-TW';
import { twMerge } from 'tailwind-merge';
import MessageInput from './MessageInput';
import MessageList from 'flow/components/chat/v2/MessageList';
import ActiveProfileStatus from 'flow/components/ui-components/ActiveProfileStatus';

const NewChatPage = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { openModal } = useModal();
    const { isSinglePaneMode } = useSinglePaneMode();
    const [chosenExtensions, setChosenExtensions] = useState<
        TypedMap<Extension>[]
    >([]);
    const scrollViewRef = useRef<HTMLDivElement>(null);

    // Add useEffect to scroll to bottom when we add a new member
    useEffect(() => {
        if (scrollViewRef.current) {
            scrollViewRef.current.scrollTop =
                scrollViewRef.current.scrollHeight;
        }
    }, [chosenExtensions]);

    return (
        <div className="flex size-full flex-col bg-white">
            <header className="flex h-fit w-full flex-col gap-x-1 gap-y-2 border-b px-6 py-2 sm:min-h-16 sm:flex-row sm:items-center sm:gap-x-4 sm:py-0">
                {isSinglePaneMode && (
                    <div className="flex">
                        <BackButton
                            onClick={() => history.push(routes['messages'])}
                        />
                    </div>
                )}
                <p className="text-base/5">{`${i18next.t('call.to')}:`}</p>

                <div
                    ref={scrollViewRef}
                    className="max-h-56 flex-1 overflow-y-scroll py-3"
                >
                    <ContactInput
                        chosenExtensions={chosenExtensions}
                        setChosenExtensions={setChosenExtensions}
                    />
                </div>

                <Button
                    className="text-main max-w-[90px] text-sm/4 font-medium sm:min-w-[120px]"
                    onClick={() =>
                        openModal('manageRoom', {
                            chosenExtensions: List(chosenExtensions),
                            header: i18next.t('Chat.Create.Room'),
                            confirmButtonText: i18next.t('Chat.Create.Room'),
                        })
                    }
                    disabled={chosenExtensions.length < 1}
                >
                    {i18next.t('Chat.Create.Room')}
                </Button>
            </header>
            {chosenExtensions.length > 0 && (
                <NewChatView extensions={chosenExtensions} />
            )}
        </div>
    );
};

const ContactInput = ({
    chosenExtensions,
    setChosenExtensions,
}: {
    chosenExtensions: TypedMap<Extension>[];
    setChosenExtensions: (newChosenExtensions: TypedMap<Extension>[]) => void;
}) => {
    const { i18next } = useAppContext();
    const [searchText, setSearchText] = useState('');
    const [selectorState, setSelectorState] = useState<PopoverState>({
        filter: [],
    });

    const inputRef = useAutoFocus();
    const addExtension = () => {
        if (selectorState.proposedExtension) {
            setChosenExtensions([
                ...chosenExtensions,
                selectorState.proposedExtension,
            ]);
            setSearchText('');
            setSelectorState({ filter: [] });
            inputRef.current?.focus();
        }
    };

    return (
        <>
            <div className="flex flex-wrap gap-x-3 gap-y-2">
                {chosenExtensions.map((extension, index) => (
                    <ExtensionPill
                        key={extension.get('key')}
                        extension={extension}
                        onRemove={() => {
                            setChosenExtensions([
                                ...chosenExtensions.slice(0, index),
                                ...chosenExtensions.slice(index + 1),
                            ]);
                            inputRef.current?.focus();
                        }}
                    />
                ))}
                <input
                    className="min-w-4 flex-1 border-0 bg-transparent p-0 text-base/5 placeholder:text-gray-500 focus:ring-0 focus:outline-hidden"
                    ref={inputRef as React.MutableRefObject<HTMLInputElement>}
                    type="search"
                    placeholder={
                        chosenExtensions.length === 0
                            ? i18next.t('input.add_members')
                            : undefined
                    }
                    value={searchText}
                    onChange={(e) => {
                        const text = e.target.value;
                        setSearchText(text);
                        setSelectorState({
                            ...selectorState,
                            filter: text
                                .split(/\s+/)
                                .filter((w) => w !== '')
                                .map((w) => w.toLowerCase()),
                        });
                    }}
                    onKeyDown={(e) => {
                        switch (e.key) {
                            case 'ArrowDown':
                                setSelectorState({
                                    ...selectorState,
                                    extensionOffset:
                                        selectorState.extensionOffset + 1,
                                });
                                e.preventDefault();
                                break;
                            case 'ArrowUp':
                                setSelectorState({
                                    ...selectorState,
                                    extensionOffset:
                                        selectorState.extensionOffset - 1,
                                });
                                e.preventDefault();
                                break;
                            case 'Backspace':
                                if (
                                    (e.target as HTMLInputElement).value ===
                                        '' &&
                                    chosenExtensions.length > 0
                                ) {
                                    setChosenExtensions(
                                        chosenExtensions.slice(
                                            0,
                                            chosenExtensions.length - 1,
                                        ),
                                    );
                                }
                                break;
                            case 'Enter':
                                addExtension();
                                e.preventDefault();
                                break;
                        }
                    }}
                />
            </div>
            <ContactSelectionPopover
                state={selectorState}
                updateState={setSelectorState}
                exclude={chosenExtensions}
                onChoose={addExtension}
                ref={inputRef}
            />
        </>
    );
};

interface PopoverState {
    filter: string[];
    extensionOffset?: number;
    proposedExtension?: TypedMap<Extension>;
}

const ContactSelectionPopover = forwardRef(
    (
        {
            state,
            exclude,
            updateState,
            onChoose,
        }: {
            state: PopoverState;
            exclude: TypedMap<Extension>[];
            updateState: (newState: PopoverState) => void;
            onChoose: () => void;
        },
        ref,
    ) => {
        const [popperElement, setPopperElement] = useState<HTMLElement | null>(
            null,
        );
        const referenceElement = ref as React.MutableRefObject<Element | null>;
        const { styles, attributes } = usePopper(
            referenceElement.current,
            popperElement,
            {
                placement: 'bottom-start',
                modifiers: [
                    {
                        name: 'flip',
                        enabled: true,
                        options: {
                            flipVariations: true,
                            allowedAutoPlacements: ['top', 'bottom'],
                        },
                    },
                ],
            },
        );

        const hasChatCapability = (e: TypedMap<Extension>) =>
            getExtensionHasCapability(e, Capabilities.CHAT);

        // Filter extensions that we should show.
        const allExtensions = useAppSelector((state) => {
            const extensions = new Array<TypedMap<Extension>>();
            state.extensions
                .get('extensions')
                ?.forEach((e) => extensions.push(e));
            return extensions;
        });
        const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
        const filteredExtensions = useMemo(
            () =>
                allExtensions
                    .filter((e) => {
                        return (
                            e.get('key') !== ownExtensionKey &&
                            !exclude.some(
                                (existing) =>
                                    existing.get('key') === e.get('key'),
                            )
                        );
                    })
                    .filter(hasChatCapability)
                    .filter((e: TypedMap<Extension>) =>
                        filterByAllfileds(e, state.filter),
                    )
                    .sort(sortByName),
            [allExtensions, state.filter, exclude, ownExtensionKey],
        );

        // The displayed extensions (accounting for arrow key handling), as well as the current offset
        // of the extensions we show.
        const [displayedExtensions, setDisplayedExtensions] =
            useState<List<TypedMap<Extension>>>();
        const [offsetStart, setOffsetStart] = useState(0);
        const maxExtensions = 6;
        useEffect(() => {
            // If no extensions to show, don't go any further, and clear state.
            if (state.filter.length === 0 || filteredExtensions.length === 0) {
                setDisplayedExtensions(undefined);
                setOffsetStart(0);
                updateState({ filter: state.filter });
                return;
            }

            // Make sure extension offset is in range; reset to 0 if not (or if unset), and determine
            // correct extension candidate. Update state if needed.
            const newExtensionOffset = Math.min(
                Math.max(state.extensionOffset || 0, 0),
                filteredExtensions.length - 1,
            );
            const newProposedExtension = filteredExtensions[newExtensionOffset];
            if (
                state.extensionOffset !== newExtensionOffset ||
                state.proposedExtension?.get('key') !==
                    newProposedExtension.get('key')
            ) {
                updateState({
                    ...state,
                    extensionOffset: newExtensionOffset,
                    proposedExtension: newProposedExtension,
                });
            }

            // Make sure the offset start doesn't result in us showing less than the
            // desired number of extensions, and that the extension we are currently on
            // is in the window.
            let newOffsetStart = offsetStart;
            if (filteredExtensions.length - offsetStart < maxExtensions) {
                newOffsetStart = 0;
            }
            if (newExtensionOffset < offsetStart) {
                // We're too far down the list; move up.
                newOffsetStart = newExtensionOffset;
            } else if (newExtensionOffset >= offsetStart + maxExtensions) {
                // We're too far up the list; move down.
                newOffsetStart = newExtensionOffset - maxExtensions + 1;
            }
            setDisplayedExtensions(
                filteredExtensions.slice(
                    newOffsetStart,
                    newOffsetStart + maxExtensions,
                ),
            );
            if (newOffsetStart !== offsetStart) {
                setOffsetStart(newOffsetStart);
            }
        }, [state.filter, state.extensionOffset || -1, offsetStart]);

        const { i18next } = useAppContext();
        const statuses = useAppSelector((state) =>
            state.extensions.get('statuses'),
        );

        return (
            <Popover>
                {displayedExtensions && (
                    <PopoverPanel
                        static={true}
                        ref={setPopperElement}
                        style={styles.popper}
                        {...attributes.popper}
                        className="z-30 w-80 truncate rounded-sm bg-white p-2 shadow-lg outline-hidden"
                    >
                        <div className="flex flex-col">
                            {displayedExtensions.map((extension, index) => {
                                const contact = extension.get('contact');
                                const name = extractName(
                                    contact,
                                    i18next.t('Contact.Unknown'),
                                );
                                const active =
                                    extension.get('key') ===
                                    state.proposedExtension?.get('key');
                                return (
                                    <div
                                        key={extension.get('chatUserKey')}
                                        className={twMerge(
                                            'group flex h-12 w-full cursor-pointer items-center gap-x-4 rounded-sm p-2',
                                            active ? 'bg-gray-200' : '',
                                        )}
                                        onMouseEnter={() =>
                                            updateState({
                                                ...state,
                                                extensionOffset:
                                                    offsetStart + index,
                                            })
                                        }
                                        onWheel={(e) => {
                                            const notches =
                                                Math.ceil(
                                                    Math.abs(e.deltaY / 120),
                                                ) * (e.deltaY < 0 ? -1 : 1);
                                            const maxOffsetStart = Math.max(
                                                filteredExtensions.length -
                                                    maxExtensions,
                                                0,
                                            );
                                            setOffsetStart(
                                                Math.min(
                                                    Math.max(
                                                        offsetStart + notches,
                                                        0,
                                                    ),
                                                    maxOffsetStart,
                                                ),
                                            );
                                        }}
                                        onClick={() => {
                                            updateState({
                                                ...state,
                                                proposedExtension: extension,
                                            });
                                            onChoose();
                                        }}
                                    >
                                        <ContactAvatar
                                            contact={contact}
                                            size="medium"
                                            status={
                                                statuses.get(
                                                    extension.get('key'),
                                                ) || 'unknown'
                                            }
                                        />
                                        <div className="flex flex-col truncate">
                                            <p className="w-full truncate overflow-hidden text-base/4 whitespace-nowrap">
                                                {name}
                                            </p>
                                            {active && (
                                                <p className="w-full truncate overflow-hidden text-sm/4 whitespace-nowrap text-gray-500">
                                                    <ActiveProfileStatus
                                                        extensionKey={extension?.get(
                                                            'key',
                                                        )}
                                                    />
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </PopoverPanel>
                )}
            </Popover>
        );
    },
);

function ExtensionPill({
    extension,
    onRemove,
}: {
    extension: TypedMap<Extension>;
    onRemove: () => void;
}) {
    const { i18next } = useAppContext();
    const contact = extension.get('contact');
    return (
        <div className="flex items-center gap-x-2 rounded-sm border border-gray-300 p-1 hover:bg-gray-200">
            <ContactAvatar contact={contact} size="small" />
            <p className="text-sm">
                {extractAbbreviatedName(contact, i18next.t('Contact.Unknown'))}
            </p>
            <button aria-label={i18next.t('remove')} onClick={onRemove}>
                <Icon iconKey="cancel" className="text-gray-600" />
            </button>
        </div>
    );
}

const NewChatView = ({ extensions }: { extensions: TypedMap<Extension>[] }) => {
    // Find any existing chat session.
    const { ownExtension } = useExtension();
    const existingSessions = useAppSelector((state) =>
        state.chats
            .get('sessions')
            .filter((s) => s.get('roomType') === 'session'),
    );

    const [chatSessionKey, setChatSessionKey] = useState<ChatSessionKey>();
    useEffect(() => {
        const wantedMembers = [...extensions, ownExtension].map((e) =>
            e.get('chatUserKey'),
        );
        const foundSession: TypedMap<ChatSession> | undefined =
            existingSessions?.find((existing: TypedMap<ChatSession>) => {
                const gotMembers = existing.get('members');
                return (
                    wantedMembers.length === gotMembers.size &&
                    wantedMembers.every((wanted) =>
                        gotMembers.find(
                            (m: TypedMap<ChatMember>) =>
                                m.get('key') === wanted,
                        ),
                    )
                );
            });
        setChatSessionKey(foundSession?.get('key'));
    }, [existingSessions, extensions, ownExtension]);

    // Show message list and input, redirecting on message send, for existing chats, or an input
    // that will create a chat session for a new chat.
    const history = useHistory();
    const dispatch = useAppDispatch();
    const clientKey = useAppSelector((state) =>
        state.chats.getIn(['client', 'key']),
    );
    return chatSessionKey ? (
        <>
            <MessageList chatSessionKey={chatSessionKey} />
            <footer className="flex flex-col items-center bg-white px-4 py-3">
                <MessageInput
                    chatSession={chatSessionKey}
                    afterSend={() =>
                        history.push(
                            generatePath(routes['messages.id'], {
                                id: chatSessionKey,
                            }),
                        )
                    }
                    preventInitialFocus={true}
                />
            </footer>
        </>
    ) : (
        <>
            <div className="flex-1" />
            <footer className="flex flex-col items-center bg-white px-4 py-3">
                <MessageInput
                    preventInitialFocus={true}
                    onSend={(text, attachment, alsoAsSMS) =>
                        dispatch(
                            createAndShowChatSessionWithMessage(
                                {
                                    members: extensions.map((e) =>
                                        Map({ key: e.get('chatUserKey') }),
                                    ),
                                },
                                clientKey,
                                text,
                                attachment,
                                (newSession: ChatSessionKey) =>
                                    history.push(
                                        generatePath(routes['messages.id'], {
                                            id: newSession,
                                        }),
                                    ),
                                alsoAsSMS,
                            ),
                        )
                    }
                    totalNewChatMembers={
                        extensions.filter((extension) => extension.get('key'))
                            .length
                    }
                />
            </footer>
        </>
    );
};

const sortByName = (a: TypedMap<Extension>, b: TypedMap<Extension>) => {
    const aName = extractName(a.get('contact'));
    const bName = extractName(b.get('contact'));
    return aName < bName ? -1 : 1;
};

export default NewChatPage;
